import React, { useEffect } from 'react';
import { buildVideoPosterUrl, buildVideoSrc } from '../../utils/chatVideo';
import useMediaReveal from '../../hooks/useMediaReveal';

function isUsablePoster(poster) {
    if (!poster) return false;
    if (/\.(mp4|mov|webm|m4v|mpg|mpeg)(\?|$)/i.test(poster)) return false;
    if (poster.startsWith('blob:') || poster.startsWith('data:video')) return false;
    return true;
}

function PreviewFrame({ src, poster }) {
    const usePoster = isUsablePoster(poster);
    const reveal = useMediaReveal(usePoster ? poster : src);
    const ref = reveal.ref;

    useEffect(() => {
        const el = ref.current;
        if (!el || usePoster) return undefined;
        const showFrame = () => {
            try {
                if (!Number.isFinite(el.duration) || el.duration <= 0) return;
                if (el.currentTime < 0.05) {
                    el.currentTime = Math.min(0.15, el.duration * 0.05);
                }
            } catch (_) { /* ignore seek errors on some codecs */ }
        };
        if (el.readyState >= 1) showFrame();
        el.addEventListener('loadedmetadata', showFrame);
        el.addEventListener('loadeddata', showFrame);
        return () => {
            el.removeEventListener('loadedmetadata', showFrame);
            el.removeEventListener('loadeddata', showFrame);
        };
    }, [src, usePoster, ref]);

    if (usePoster) {
        return (
            <img
                ref={ref}
                className={`article-video-preview-media ${reveal.className}`}
                src={poster}
                alt=""
                onLoad={reveal.done}
                onError={reveal.fail}
            />
        );
    }
    return (
        <video
            ref={ref}
            className={`article-video-preview-media ${reveal.className}`}
            src={src}
            muted
            playsInline
            preload="metadata"
            onLoadedData={reveal.done}
            onSeeked={reveal.done}
            onError={reveal.fail}
        />
    );
}

export function SmallPlayButton({ size = 40 }) {
    return (
        <svg
            className="article-video-play"
            width={size}
            height={size}
            viewBox="0 0 48 48"
            aria-hidden="true"
        >
            <circle cx="24" cy="24" r="22" fill="rgba(0,0,0,0.5)" />
            <circle cx="24" cy="24" r="22" fill="none" stroke="#fff" strokeWidth="2.25" />
            <path d="M20 14.5v19L36 24z" fill="#fff" />
        </svg>
    );
}

export default function VideoPreviewThumb({ item, className, style, playSize = 40 }) {
    const blobUrl = item?.url && (String(item.url).startsWith('blob:') || String(item.url).startsWith('data:'))
        ? item.url
        : '';
    const local = !!(item?.local || blobUrl);
    const src = blobUrl || buildVideoSrc(
        local,
        item?.fileUrl || item?.url || item?.href,
        item?.attachedFileName || item?.originalName || item?.name
    );
    const poster = buildVideoPosterUrl(local, item?.thumbnailUrl, item?.fileUrl || item?.url || item?.href);

    return (
        <div className={`article-video-preview${className ? ` ${className}` : ''}`} style={style}>
            <PreviewFrame src={src} poster={poster} />
            <div className="article-video-play-wrap">
                <SmallPlayButton size={playSize} />
            </div>
        </div>
    );
}
